import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, ArrowDown, Paperclip, Square, Upload, Plus } from 'lucide-react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Toast from '../common/Toast';
import AudioRecorder from '../common/AudioRecorder';
import FileAttachment from './message/FileAttachment';
import { sendMessage, fetchChatHistory, stopConversation, regenerateMessage } from '../../api/chats';
import { sendAgentMessage, fetchAgentChatHistory, fetchAgentChatHistoryByToken } from '../../api/agents/agent-chats';
import { uploadFile, getFileUrl } from '../../api/files';
import { getAcceptedFileTypes } from '../../constants/fileTypes';
import Message from './message/Message';
import DragAndDrop from '../common/DragAndDrop';
import { STATUS_TYPES } from '../../constants/statusTypes';
import ModelSelector from './ModelSelector';
import { useSidebar } from '../../contexts/SidebarContext';
import { useMobile } from '../../contexts/MobileContext';
import ServicesSelector from './ServicesSelector';
import Button from '../common/button/Button';
import AgentIntroCard from '../agents/chats/AgentIntroCard';
import ServiceLogo from '../common/logo/ServiceLogo';
import TooltipPortal from '../common/tooltip/TooltipPortal';

// Hooks
import { useMessageTree } from './hooks/useMessageTree';
import { useAutoScroll } from './hooks/useAutoScroll';
import { useFileAttachments } from './hooks/useFileAttachments';
import { useModelState } from './hooks/useModelState';
import { useAudioTranscription } from './hooks/useAudioTranscription';

import styles from './styles/ChatArea.module.css';
import homeStyles from '../home/styles/Home.module.css';

const ChatArea = ({
    setChats = () => {},
    setSelectedChatId = () => {},
    currentChatTitle = null,
    // Agent chat props
    agent = null,
    isAgentChat = false,
    onBackToAgent = null,
    isGuest = false,
}) => {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isSidebarCollapsed } = useSidebar();
    const { isMobile, isPwa, isKeyboardVisible } = useMobile();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isInitialState, setIsInitialState] = useState(() => !chatId || chatId === 'new');
    const { autoScrollEnabled, setAutoScrollEnabled, shouldShowButton, messagesContainerRef, scrollToBottom } = useAutoScroll();
    const [isUpdateHistory, setIsUpdateHistory] = useState(true);
    const textareaRef = useRef(null);
    const inputContainerRef = useRef(null);
    const [copiedCode, setCopiedCode] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [guestChatToken, setGuestChatToken] = useState(null);
    const [agentChatMeta, setAgentChatMeta] = useState({ isParticipant: false, isOwner: false });
    const [hoveredNewChatGuest, setHoveredNewChatGuest] = useState(false);

    const { activeMessages, switchSibling, lastMessageId } = useMessageTree({ messages, isAgentChat });
    const { selectedModel, handleModelChange, isImageUploadEnabled, isReasoningModel } = useModelState({ activeMessages });

    const {
        attachments,
        setAttachments,
        isAnyFileUploading,
        handleFileSelect,
        handleRemoveFile,
        handleFileDrop,
        resetAttachments,
        fileInputRef,
    } = useFileAttachments({
        isAgentChat,
        isImageUploadEnabled,
        uploadFileFn: uploadFile,
        setToastMessage,
    });

    const hasContent = useMemo(() => {
        return inputValue.trim().length > 0 || attachments.length > 0;
    }, [inputValue, attachments]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);

    // Read guest token from URL for guest agent chats
    useEffect(() => {
        if (!(isAgentChat && isGuest)) return;
        try {
            const params = new URLSearchParams(location.search);
            const tokenFromUrl = params.get('guest_token');
            if (tokenFromUrl && tokenFromUrl !== guestChatToken) {
                setGuestChatToken(tokenFromUrl);
            }
        } catch (e) {
            // ignore malformed query
        }
    }, [location.search, isAgentChat, isGuest, guestChatToken]);

    const updateDocumentTitle = useCallback(title => {
        if (isAgentChat && agent) {
            document.title = agent.name ? `JetChats - Chat with ${agent.name}` : 'JetChats - Agent Chat';
        } else {
            document.title = title ? title : 'JetChats';
        }
    }, [isAgentChat, agent]);

    useEffect(() => {
        if (currentChatTitle) {
            updateDocumentTitle(currentChatTitle);
        }
    }, [currentChatTitle, updateDocumentTitle]);

    const processError = useCallback((error, messageId = null) => {
        if (messageId) {
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
        }
        setMessages(prev => prev.filter(msg => !msg.isTyping && !msg.status));
        setIsGenerating(false);

        setToastMessage({
            message: error.error || 'Failed to send message',
            type: 'error',
            duration: 5000,
        });
    }, [setToastMessage]);

    /* ---- SEND, EDIT, REGENERATE AND RECEIVE MESSAGE START ---- */
    const onSendMessage = useCallback(({ update, isNewChat, tempChatId = null }) => {
        switch (update.type) {
            case 'status':
                setMessages(prev => {
                    // Remove all previous status messages
                    const messagesWithoutStatus = prev.filter(msg => !msg.status);
                    const statusMessage = prev[prev.length - 1];

                    const assistantSender = isAgentChat ? 'agent' : 'assistant';
                    return [...messagesWithoutStatus, {
                        id: update.messageId || statusMessage?.id || `status-${Date.now()}`,
                        status: update.status,
                        sender: assistantSender,
                        isTyping: false,
                        parentId: statusMessage?.sender === assistantSender ? statusMessage.parentId : null,
                        createdAt: new Date().toISOString(),
                        tools: statusMessage?.tools,
                        agent: statusMessage?.agent || null,
                    }];
                });
                break;

            case 'init':
                if (isNewChat) {
                    // For guests, do not reveal chatId nor navigate; keep staying on slug route
                    if (isAgentChat && isGuest) {
                        if (update.content?.guestChatToken) {
                            setGuestChatToken(update.content.guestChatToken);
                            // Reflect guest token in URL for easy sharing/refreshing
                            if (agent?.slug) {
                                navigate(`/agent/${agent.slug}?guest_token=${encodeURIComponent(update.content.guestChatToken)}`, { replace: true });
                            }
                        }
                        setIsUpdateHistory(false);
                    } else {
                        const newChat = {
                            id: update.content.chatId,
                            title: 'New Chat',
                            updatedAt: new Date().toISOString(),
                        };
                        setChats(prevChats => [
                            newChat,
                            ...prevChats.filter(chat => chat.id !== tempChatId),
                        ]);
                        setSelectedChatId(update.content.chatId);
                        setIsUpdateHistory(false);
                        navigate(isAgentChat && agent ? `/a/${update.content.chatId}` : `/c/${update.content.chatId}`, { replace: true });
                    }
                }

                const newUserMessage = update.content?.userMessage;
                const chatHistory = update.content?.chatHistory;
                setMessages(prev => {
                    // For new agent chats, server can send full chat history (including welcome message).
                    // Replace local placeholders with server history to keep parent-child relations consistent.
                    if (isAgentChat && Array.isArray(chatHistory) && newUserMessage) {
                        const assistantSender = isAgentChat ? 'agent' : 'assistant';
                        const lastMessage = prev[prev.length - 1];
                        const statusMessage = (lastMessage?.sender === assistantSender && lastMessage?.status)
                            ? lastMessage
                            : null;

                        const merged = [...chatHistory];
                        if (statusMessage) {
                            merged.push({
                                ...statusMessage,
                                parentId: newUserMessage.id,
                            });
                        }
                        return merged;
                    }

                    // Fallback: update only the user and assistant placeholder
                    const lastUserMessage = prev[prev.length - 2]; // Get the user message
                    if (lastUserMessage?.sender === 'user') {
                        return [
                            ...prev.slice(0, -2),
                            {
                                ...lastUserMessage,
                                ...newUserMessage, // Update user message
                            },
                            {
                                ...prev[prev.length - 1],
                                parentId: newUserMessage.id, // Update assistant parentId
                            },
                        ];
                    }
                    return prev;
                });
                break;

            case 'content':
                setMessages(prev => {
                    // Remove status message if exists
                    const statusMessage = prev[prev.length - 1];
                    const messagesWithoutStatus = prev.filter(msg => !msg.status);

                    const currentMessage = messagesWithoutStatus[messagesWithoutStatus.length - 1];
                    const assistantSender = isAgentChat ? 'agent' : 'assistant';
                    if (currentMessage?.sender === assistantSender && currentMessage.isTyping) {
                        return [...messagesWithoutStatus.slice(0, -1), {
                            ...currentMessage,
                            text: (currentMessage.text || '') + update.content,
                            isTyping: true,
                        }];
                    }

                    return [...messagesWithoutStatus, {
                        id: update.messageId || statusMessage?.id || `typing-${Date.now()}`,
                        text: update.content,
                        reasoning_content: statusMessage?.reasoning_content,
                        sender: assistantSender,
                        isTyping: true,
                        parentId: statusMessage?.sender === assistantSender ? statusMessage.parentId : null,
                        createdAt: new Date().toISOString(),
                        metadata: statusMessage?.metadata,
                        tools: statusMessage?.tools,
                        agent: statusMessage?.agent || null,
                    }];
                });
                break;

            case 'chat_title':
                if (update.content.chatTitle) {
                    const newTitle = update.content.chatTitle;
                    setChats(prevChats => prevChats.map(chat =>
                        chat.id === update.content.chatId ? {
                            ...chat,
                            title: newTitle,
                        } : chat
                    ));
                    updateDocumentTitle(newTitle);
                }
                break;

            case 'complete':
                setIsGenerating(false);
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    const assistantSender = isAgentChat ? 'agent' : 'assistant';
                    if (lastMessage?.sender === assistantSender) {
                        return [...prev.slice(0, -1), {
                            ...lastMessage,
                            id: update.content.assistantMessage.id,
                            text: update.content.assistantMessage.content,
                            reasoning_content: update.content.assistantMessage.reasoning_content,
                            isTyping: false,
                            status: null,
                            metadata: update.content.assistantMessage.metadata,
                            parentId: update.content.assistantMessage.parentId,
                            createdAt: update.content.assistantMessage.createdAt,
                            tools: update.content.assistantMessage.tools,
                            isUsingTool: false,
                            agent: update.content.assistantMessage.agent || lastMessage.agent,
                        }];
                    }
                    return prev;
                });

                if (!(isAgentChat && isGuest)) {
                    setIsUpdateHistory(true);
                }
                break;

            case 'reasoning_time':
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    const assistantSender = isAgentChat ? 'agent' : 'assistant';
                    if (lastMessage?.sender === assistantSender) {
                        return [...prev.slice(0, -1), {
                            ...lastMessage,
                            metadata: {
                                ...lastMessage.metadata,
                                reasoningTime: update.time,
                            }
                        }];
                    }
                    return prev;
                });
                break;

            case 'reasoning':
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    const assistantSender = isAgentChat ? 'agent' : 'assistant';
                    if (lastMessage?.sender === assistantSender) {
                        return [...prev.slice(0, -1), {
                            ...lastMessage,
                            reasoning_content: (lastMessage.reasoning_content || '') + update.content,
                        }];
                    }
                    return prev;
                });
                break;

            case 'using_tool':
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    const assistantSender = isAgentChat ? 'agent' : 'assistant';
                    if (lastMessage?.sender === assistantSender) {
                        const tools = lastMessage.tools || [];

                        if (update.content.status === 'processing') {
                            // Add new tool
                            return [...prev.slice(0, -1), {
                                ...lastMessage,
                                status: STATUS_TYPES.LOADING,
                                tools: [...tools, update.content],
                                isUsingTool: true,
                            }];
                        } else if (update.content.status === 'complete') {
                            // Trigger tools refetch by bumping a refresh key
                            const toolsRefreshKey = (lastMessage.toolsRefreshKey || 0) + 1;
                            return [...prev.slice(0, -1), {
                                ...lastMessage,
                                tools: tools.map(tool => 
                                    tool.randomId === update.content.randomId
                                        ? {
                                            serviceName: tool.serviceName,
                                            serviceLogo: tool.serviceLogo,
                                            task: tool.task,
                                            randomId: tool.randomId,
                                        }
                                        : tool
                                ),
                                toolsRefreshKey,
                            }];
                        } else if (update.content.status === 'done') {
                            return [...prev.slice(0, -1), {
                                ...lastMessage,
                                isUsingTool: false,
                            }];
                        }
                    }
                    return prev;
                });
                break;

            case 'error':
                processError(update.content);
                break;

            default:
                console.warn('Unknown update type:', update.type);
                break;
        }
    }, [navigate, setChats, setSelectedChatId, updateDocumentTitle, processError, isAgentChat, agent, isGuest]);

    const sendChatMessage = useCallback(({
        text, model, attachments, isNewChat, tempChatId = null, parentId = null,
    }) => {
        if (isAgentChat && agent) {
            const chatIdForSend = (isGuest && (!chatId || chatId === 'new')) ? 'new' : chatId;
            setAgentChatMeta(prev => ({ ...prev, isParticipant: true }));
            sendAgentMessage(chatIdForSend, agent.id, {
                content: text,
                parentId,
                guestChatToken: (isAgentChat && isGuest) ? guestChatToken : null,
            }, update => onSendMessage({ update, isNewChat, tempChatId }))
                .catch(error => processError(error));
        } else {
            sendMessage(chatId, {
                content: text,
                model: model,
                attachments: attachments?.map(file => ({
                    id: file.id,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                })),
                parentId,
            }, update => onSendMessage({ update, isNewChat, tempChatId }))
                .catch(error => processError(error));
        }
    }, [chatId, onSendMessage, processError, isAgentChat, agent, isGuest, guestChatToken]);

    // Function to update chat position in the list
    const updateChatPosition = useCallback(chatId => {
        if (!chatId || chatId === 'new') return;

        setChats(prevChats => {
            const updatedChats = prevChats.filter(chat => chat.id !== chatId);
            const chatToUpdate = prevChats.find(chat => chat.id === chatId);
            if (chatToUpdate) {
                return [{
                    ...chatToUpdate,
                    updatedAt: new Date().toISOString(),
                }, ...updatedChats];
            }
            return prevChats;
        });
    }, [setChats]);

    const handleSubmitMessage = useCallback(() => {
        setIsGenerating(true);

        const trimmedValue = inputValue.trim();
        if ((!trimmedValue && attachments.length === 0) || isAnyFileUploading) return;

        const isNewChat = !chatId || chatId === 'new';
        const tempChatId = isNewChat ? `temp-${Date.now()}` : chatId;

        // Optional agent welcome message (local UI only) for agent chats on first message
        const hasWelcome = isAgentChat && isNewChat && agent?.welcomeMessage && agent.welcomeMessage.trim().length > 0;
        const dateNow = Date.now();
        const welcomeMessage = hasWelcome ? {
            id: `assistant-${dateNow}`,
            text: agent.welcomeMessage.trim(),
            sender: 'agent',
            parentId: lastMessageId,
            createdAt: new Date(dateNow).toISOString(),
            agent,
        } : null;

        // Add user message
        const userMessage = {
            id: `user-${dateNow + 1}`,
            text: trimmedValue,
            sender: 'user',
            attachments: attachments?.map(file => ({
                id: file.id,
                name: file.name,
                type: file.type,
                size: file.size,
                url: getFileUrl(`/api/files/${file.id}`),
            })),
            parentId: hasWelcome ? welcomeMessage.id : lastMessageId,
            createdAt: new Date(dateNow + 1).toISOString(),
        };

        const assistantMessage = {
            id: `assistant-${dateNow + 2}`,
            status: STATUS_TYPES.LOADING,
            sender: isAgentChat ? 'agent' : 'assistant',
            isTyping: false,
            parentId: userMessage.id,
            createdAt: new Date(dateNow + 2).toISOString(),
            agent: isAgentChat ? agent : null,
        };

        setMessages(prev => hasWelcome
            ? [...prev, welcomeMessage, userMessage, assistantMessage]
            : [...prev, userMessage, assistantMessage]);
        setInputValue('');
        setAttachments([]);
        setIsInitialState(false);

        requestAnimationFrame(() => {
            scrollToBottom('instant');
        });

        // Reset textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        // If it's a new chat, add it to the list immediately
        if (isNewChat) {
            const tempChat = {
                id: tempChatId,
                title: 'New Chat',
                updatedAt: new Date().toISOString(),
            };
            setChats(prevChats => [tempChat, ...prevChats]);
            setSelectedChatId(tempChatId);
        } else {
            // Update existing chat's position
            updateChatPosition(chatId);
        }

        sendChatMessage({
            text: trimmedValue,
            model: selectedModel,
            attachments,
            isNewChat,
            tempChatId,
            parentId: lastMessageId,
        });
    }, [
        inputValue,
        chatId,
        setChats,
        setSelectedChatId,
        attachments,
        isAnyFileUploading,
        scrollToBottom,
        selectedModel,
        sendChatMessage,
        lastMessageId,
        updateChatPosition,
        isAgentChat,
        agent,
        setAttachments,
    ]);

    const handleEditMessage = useCallback((editedMessage, newText) => {
        // Find the index of the edited message
        const messageIndex = messages.findIndex(msg => msg.id === editedMessage.id);
        if (messageIndex === -1) return;

        // Create a new message object with updated text
        const updatedMessage = {
            ...editedMessage,
            id: `user-${Date.now()}`,
            text: newText,
            createdAt: new Date().toISOString(),
        };

        // Remove all messages after the edited message
        setMessages(prev => [...prev, updatedMessage]);

        // Update chat position when editing message
        updateChatPosition(chatId);

        try {
            // Send the message like a new message with parentId
            const assistantMessage = {
                id: `assistant-${Date.now()}`,
                status: STATUS_TYPES.LOADING,
                sender: isAgentChat ? 'agent' : 'assistant',
                isTyping: false,
                parentId: editedMessage.parentId,
                createdAt: new Date().toISOString(),
                agent: isAgentChat ? agent : null,
            };

            setMessages(prev => [...prev, assistantMessage]);
            setIsGenerating(true);

            sendChatMessage({
                text: newText,
                model: editedMessage.metadata?.model || selectedModel,
                attachments: [],
                isNewChat: false,
                parentId: editedMessage.parentId,
            });
        } catch (error) {
            console.error('Error editing message:', error);
            setToastMessage({
                message: 'Failed to edit message',
                type: 'error',
            });
        } finally {
            setIsGenerating(false);
        }
    }, [selectedModel, messages, sendChatMessage, chatId, updateChatPosition, isAgentChat, agent]);

    const handleRegenerateMessage = useCallback(({ message, newModel }) => {
        setIsGenerating(true);

        const assistantMessage = {
            id: `assistant-${Date.now()}`,
            status: STATUS_TYPES.LOADING,
            sender: isAgentChat ? 'agent' : 'assistant',
            isTyping: false,
            parentId: message.parentId,
            createdAt: new Date().toISOString(),
            agent: isAgentChat ? agent : null,
        };

        setMessages(prev => [...prev, assistantMessage]);

        requestAnimationFrame(() => {
            scrollToBottom('instant');
        });

        // Update chat position when regenerating message
        updateChatPosition(chatId);

        regenerateMessage(chatId, {
            messageId: message.id,
            modelId: newModel,
            parentId: message.parentId,
        }, update => onSendMessage({ update, isNewChat: false }))
            .catch(error => processError(error, assistantMessage.id));
    }, [chatId, onSendMessage, scrollToBottom, processError, updateChatPosition, isAgentChat, agent]);
    /* ---- SEND, EDIT, REGENERATE AND RECEIVE MESSAGE END ---- */

    const handleKeyDown = useCallback(e => {
        if (e.key === 'Enter' && !isMobile) {
            if (e.shiftKey) return;
            e.preventDefault();
            handleSubmitMessage();
        }
    }, [handleSubmitMessage, isMobile]);

    const handleInput = useCallback(e => {
        setInputValue(e.target.value);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, []);

    const updateInputContainerHeight = useCallback(height => {
        if (height) {
            document.documentElement.style.setProperty('--input-container-height', `${height}px`);
        } else if (inputContainerRef.current) {
            const offsetHeight = inputContainerRef.current.offsetHeight;
            document.documentElement.style.setProperty('--input-container-height', `${offsetHeight}px`);
        }
    }, []);

    useEffect(() => {
        if (autoScrollEnabled) {
            const timeoutId = setTimeout(() => {
                scrollToBottom('smooth');
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [messages, scrollToBottom, autoScrollEnabled]);

    useEffect(() => {
        updateInputContainerHeight();

        const resizeObserver = new ResizeObserver(entries => {
            requestAnimationFrame(() => {
                updateInputContainerHeight(entries?.[0]?.contentRect?.height);
            });
        });

        if (inputContainerRef.current) {
            resizeObserver.observe(inputContainerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateInputContainerHeight]);

    const loadChatHistory = useCallback(async () => {
        if (chatId && chatId !== 'new') {
            try {
                // For guests in agent chats, skip history; only stream shows once they send
                let history;
                if (isAgentChat && isGuest) {
                    // Try to load history via guest token
                    if (guestChatToken) {
                        try {
                            const response = await fetchAgentChatHistoryByToken(guestChatToken);
                            history = Array.isArray(response) ? response : (response?.messages || []);
                            setAgentChatMeta({ isParticipant: false, isOwner: false });
                        } catch (e) {
                            // On invalid token, do not crash UI; start empty
                            history = [];
                        }
                    } else {
                        history = [];
                    }
                } else if (isAgentChat) {
                    try {
                        const response = await fetchAgentChatHistory(chatId);
                        history = Array.isArray(response) ? response : (response?.messages || []);
                        if (response && response.meta) {
                            setAgentChatMeta({
                                isParticipant: !!response.meta.isParticipant,
                                isOwner: !!response.meta.isOwner,
                            });
                        } else {
                            setAgentChatMeta({ isParticipant: false, isOwner: false });
                        }
                    } catch (e) {
                        if (e.message === 'unauthorized') {
                            // Redirect guests trying to open /a/:chatId
                            navigate('/chats', { replace: true });
                            return;
                        }
                        if (e.message === 'forbidden' || e.message === 'chat_not_found') {
                            // Authenticated but not a participant/owner
                            navigate('/agents', { replace: true });
                            return;
                        }
                        throw e;
                    }
                } else {
                    history = await fetchChatHistory(chatId);
                }

                setIsInitialState(false);
                setAutoScrollEnabled(true);
                resetAttachments();

                setTimeout(() => {
                    setMessages(history);
                    if (messagesContainerRef?.current) {
                        requestAnimationFrame(() => {
                            if (messagesContainerRef?.current) {
                                messagesContainerRef.current.scrollTo({
                                    top: messagesContainerRef.current.scrollHeight,
                                    behavior: 'instant'
                                });
                            }
                        });
                    }
                }, 150);
            } catch (error) {
                if (error.message === 'chat_not_found') {
                    setToastMessage({
                        message: `Unable to load conversation ${chatId}`,
                        type: 'error',
                    });
                    navigate(isAgentChat && agent?.slug
                        ? `/agent/${agent.slug}` : '/chats', { replace: true });
                }
            }
        } else {
            try {
                let history = [];
                // For slug route (no chatId), load via guest token if available
                if (isAgentChat && isGuest && guestChatToken) {
                    try {
                        const response = await fetchAgentChatHistoryByToken(guestChatToken);
                        history = Array.isArray(response) ? response : (response?.messages || []);
                        setAgentChatMeta({ isParticipant: false, isOwner: false });
                    } catch (e) {
                        history = [];
                    }
                }

                if (history.length > 0) {
                    setMessages(history);
                    setIsInitialState(false);
                    setAutoScrollEnabled(true);
                    resetAttachments();
                    setTimeout(() => {
                        if (messagesContainerRef?.current) {
                            messagesContainerRef.current.scrollTo({
                                top: messagesContainerRef.current.scrollHeight,
                                behavior: 'instant',
                            });
                        }
                    }, 150);
                } else {
                    setMessages([]);
                    setIsInitialState(true);
                    setAutoScrollEnabled(true);
                    resetAttachments();
                }
            } catch (_) {
                setMessages([]);
                setIsInitialState(true);
                setAutoScrollEnabled(true);
                resetAttachments();
            }
        }
    }, [chatId, navigate, isAgentChat, agent, resetAttachments, setAutoScrollEnabled, messagesContainerRef, isGuest, guestChatToken]);

    const canWriteMessages = useMemo(() => {
        if (!isAgentChat) return true;
        if (!chatId || chatId === 'new') return true;
        return !!agentChatMeta.isParticipant;
    }, [isAgentChat, chatId, agentChatMeta]);

    useEffect(() => {
        if (isUpdateHistory) {
            loadChatHistory();
            setInputValue('');

            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.style.height = 'auto';
            }
        }
    }, [chatId, loadChatHistory, isUpdateHistory]);

    const handleCopyCode = useCallback(code => {
        // Create a temporary textarea element to handle copying
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.style.position = 'fixed';  // Make it invisible
        textarea.style.opacity = 0;
        document.body.appendChild(textarea);
        textarea.select();

        try {
            // Use execCommand which works better on iOS Safari
            const successful = document.execCommand('copy');
            if (!successful) {
                // Fallback to clipboard API if execCommand fails
                navigator.clipboard.writeText(code);
            }
        } catch (err) {
            // Last resort fallback
            navigator.clipboard.writeText(code);
        } finally {
            document.body.removeChild(textarea);
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(null), 2000);
        }
    }, []);

    const handleStopGeneration = useCallback(async () => {
        try {
            await stopConversation(chatId);
            setIsGenerating(false);
        } catch (error) {
            console.error('Failed to stop generation:', error);
            setToastMessage({
                message: 'Failed to stop generation',
                type: 'error',
            });
        }
    }, [chatId]);

    const isSubmitDisabled = useMemo(() => {
        if (isAnyFileUploading || isRecording) return true;

        // Check if there are any images in attachments
        const hasImages = attachments.some(file => file.type?.startsWith('image/'));
        return hasImages && !isImageUploadEnabled;
    }, [attachments, isAnyFileUploading, isImageUploadEnabled, isRecording]);

    const disabledReason = useMemo(() => {
        if (isAnyFileUploading) return 'File uploading pending...';
        if (isRecording) return 'Recording in progress...';
        if (attachments.some(file => file.type?.startsWith('image/')) && !isImageUploadEnabled) {
            return 'Images are not supported with the current model';
        }
        return null;
    }, [attachments, isAnyFileUploading, isImageUploadEnabled, isRecording]);

    const { handleAudioRecorded } = useAudioTranscription({ textareaRef, inputValue, setInputValue, setToastMessage });

    const renderAuth = useCallback(() => {
        if (!(isAgentChat && agent && isGuest)) return null;
        return (
            <div className={styles.guestTopRow}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <a href="/" target="_blank" rel="noopener noreferrer">
                        <ServiceLogo logo={"/icons/android-icon-192x192.png"} size="small" />
                    </a>
                    {agent?.slug && (
                        <TooltipPortal tooltip={'New chat with this agent'} show={hoveredNewChatGuest}>
                            <button
                                onClick={() => window.open(`/agent/${agent.slug}`, '_blank', 'noopener,noreferrer')}
                                className={styles.topIconButton}
                                style={{ marginLeft: 8 }}
                                aria-label="New chat in new tab"
                                onMouseEnter={() => setHoveredNewChatGuest(true)}
                                onMouseLeave={() => setHoveredNewChatGuest(false)}
                            >
                                <Plus />
                            </button>
                        </TooltipPortal>
                    )}
                </div>

                {!isMobile && <div className={styles.guestHeaderButtons}>
                    <Link to="/register" className={homeStyles.ctaButton}>
                        Sign up for free
                    </Link>
                </div>}

                {isMobile && <div className={styles.guestHeaderButtons}>
                    <Link to="/login" className={homeStyles.ctaButton}>
                        Log in
                    </Link>
                </div>}
            </div>
        );
    }, [isAgentChat, agent, isGuest, isMobile, hoveredNewChatGuest]);

    const renderInputNote = useCallback(() => {
        if (!(isAgentChat && agent && isInitialState)) return null;

        let note = null;
        if (isGuest) {
            note = <p>To save your conversation history, we recommend you <Link to="/login">log in</Link>.</p>
        } else if (agent.accessType === 'REGISTERED' && agent.creditsPayer === 'USER') {
            note = 'For this agent, credits are charged to your account.';
        }

        if (note) {
            return (
                <div className={`${styles.inputNote} ${isSidebarCollapsed ? styles.inputNoteExpanded : ''}`}>
                    {note}
                </div>
            );
        }

        return null;
    }, [isAgentChat, agent, isInitialState, isGuest, isSidebarCollapsed]);

    return (
        <div className={styles.chatContainer}>
            {isAgentChat && agent && !isGuest && (
                <div className={`${styles.agentHeader} ${isSidebarCollapsed ? styles.agentHeaderCollapsed : ''}`}>
                    {onBackToAgent && (
                        <Button onClick={onBackToAgent} variant="secondary" size="small">
                            ← Back
                        </Button>
                    )}
                </div>
            )}

            <DragAndDrop
                onDrop={handleFileDrop}
                icon={<Upload size={48} />}
                overlayText={isImageUploadEnabled 
                    ? "Drop any files here to add them to the conversation"
                    : "Drop files to add them to the conversation (images not supported)"}
                overlayClassName={`${styles.customOverlay} ${isSidebarCollapsed ? styles.expanded : ''}`}
            >
                <div className={styles.messagesContainer} ref={messagesContainerRef}>
                    {renderAuth()}
                    <div className={styles.messagesArea}>
                        {activeMessages.map((message, index) => message?.active && (
                            <Message
                                key={index}
                                message={{ ...message, index }}
                                copiedCode={copiedCode}
                                onCopyCode={handleCopyCode}
                                setToastMessage={setToastMessage}
                                handleEditMessage={handleEditMessage}
                                onNavigate={direction => switchSibling(message, direction)}
                                siblings={messages.filter(msg => 
                                    msg.parentId === message.parentId && 
                                    msg.sender === message.sender
                                )}
                                handleRegenerateMessage={handleRegenerateMessage}
                                parentMessage={activeMessages?.find(msg => msg.id === message.parentId) || null}
                                isAgentChat={isAgentChat}
                                agentInfo={agent}
                                guestChatToken={(isAgentChat && isGuest) ? guestChatToken : null}
                            />
                        ))}

                        {shouldShowButton && (
                            <button
                                className={`${styles.scrollDownButton} ${isSidebarCollapsed ? styles.scrollDownButtonExpanded : ''} ${isPwa && !isKeyboardVisible ? styles.scrollDownButtonPwa : ''}`}
                                onClick={() => {
                                    if (messagesContainerRef.current) {
                                        messagesContainerRef.current.scrollTo({
                                            top: messagesContainerRef.current.scrollHeight,
                                            behavior: 'smooth',
                                        });
                                    }
                                }}
                                aria-label="Scroll down"
                            >
                                <ArrowDown size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <div
                    ref={inputContainerRef}
                    className={`${styles.inputContainer} ${isInitialState ? styles.centered : ''} ${isPwa && !isKeyboardVisible ? styles.inputContainerPwa : ''}`}
                >
                    {isAgentChat && agent && (
                        <AnimatePresence initial={true} mode="wait">
                            {isInitialState && (
                                <motion.div
                                    key={`agent-intro-${agent.id || agent.slug || 'unknown'}`}
                                    className={styles.agentIntro}
                                    initial={{ opacity: 0, y: 28, scaleX: 0.95, scaleY: 0.985, filter: 'blur(10px)' }}
                                    animate={{ opacity: 1, y: 0, scaleX: 1, scaleY: 1, filter: 'blur(0px)' }}
                                    exit={{ opacity: 0, y: 18, scaleX: 0.96, scaleY: 0.985, filter: 'blur(8px)' }}
                                    transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.9 }}
                                    layout
                                >
                                    <AgentIntroCard agent={agent} />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}

                    {(!isAgentChat || canWriteMessages) && (
                        <div className={styles.inputWrapper}>
                            {attachments.length > 0 && (
                                <div className={styles.attachmentsContainer}>
                                    {attachments.map(file => (
                                        <FileAttachment
                                            key={file.id}
                                            id={file.id}
                                            file={file.file}
                                            onRemove={handleRemoveFile}
                                            uploadProgress={file.progress}
                                        />
                                    ))}
                                </div>
                            )}

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                multiple
                                style={{ display: 'none' }}
                                accept={getAcceptedFileTypes(isImageUploadEnabled)}
                            />

                            <textarea
                                ref={textareaRef}
                                name="message"
                                value={inputValue}
                                onChange={handleInput}
                                onKeyDown={handleKeyDown}
                                placeholder={
                                    isInitialState 
                                        ? (isAgentChat && agent?.name
                                            ? `Ask ${agent.name} anything` : "Ask me anything")
                                        : "Write a message"
                                }
                                className={`${styles.textarea} ${isInitialState ? styles.textareaInitial : ''}`}
                                rows={1}
                            />

                            <div className={styles.buttonsContainer}>
                                {!isAgentChat && (
                                    <div className={styles.leftButtons}>
                                        <button
                                            className={styles.attachButton}
                                            onClick={() => fileInputRef.current?.click()}
                                            aria-label="Attach files"
                                        >
                                            <Paperclip size={18} />
                                        </button>
                                        <ServicesSelector />
                                        <ModelSelector
                                            selectedModel={selectedModel}
                                            onModelSelect={handleModelChange}
                                        />
                                    </div>
                                )}
                                <div className={styles.rightButtons}>
                                    {!isGuest && <AudioRecorder
                                        onAudioRecorded={handleAudioRecorded}
                                        onRecordingStateChange={setIsRecording}
                                    />}
                                    {(hasContent && !isGenerating && !isRecording) && (
                                        <button
                                            className={styles.sendButton}
                                            onClick={handleSubmitMessage}
                                            disabled={isSubmitDisabled}
                                            aria-label="Send message"
                                        >
                                            <ArrowUp size={18} />
                                            {disabledReason && (
                                                <div className={styles.tooltip}>
                                                    {disabledReason}
                                                </div>
                                            )}
                                        </button>
                                    )}
                                    {isGenerating && !isReasoningModel && !isAgentChat && (
                                        <button
                                            className={styles.sendButton}
                                            onClick={handleStopGeneration}
                                            aria-label="Stop generation"
                                        >
                                            <Square size={12} fill="white" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {renderInputNote()}

                {toastMessage && (
                    <Toast
                        message={toastMessage.message}
                        type={toastMessage.type}
                        onClose={() => setToastMessage(null)}
                        duration={toastMessage.duration}
                    />
                )}
            </DragAndDrop>
        </div>
    );
};

export default memo(ChatArea);
